import { useState, useEffect } from "react";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import IconButton from "@material-ui/core/IconButton";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import CloseRoundedIcon from "@material-ui/icons/CloseRounded";
import RateReviewRoundedIcon from "@material-ui/icons/RateReviewRounded";
import HistoryRoundedIcon from "@material-ui/icons/HistoryRounded";
import SyncRoundedIcon from "@material-ui/icons/SyncRounded";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileSignature,
  faFileInvoiceDollar,
  faFolderOpen,
  faMoneyBillWave,
} from "@fortawesome/free-solid-svg-icons";

import { SaleDashGenData } from "./SaleDashGenData";
import { SaleDashChecklist } from "./SaleDashChecklist";
import { SaleDashProposals } from "./SaleDashProposals";
import { SaleDashNotes } from "./SaleDashNotes";
import { SaleDashContracts } from "./SaleDashContracts";
import { SaleDashFiles } from "./SaleDashFiles";
import { SaleDashHistory } from "./SaleDashHistory";
import { SaleDashFinancings } from "./SaleDashFinancings";
import { SaleDashFinancingsMarketplace } from "../../sales/components/SaleDashFinancingsMarketplace";
import { SaleDashServices } from "./SaleDashServices";
import { useHttpClient } from "../../shared/hooks/httpHook";
import { ModalError } from "../../shared/components/UIElements/ModalError";
import { LoadingSpinnerOverlayRegular } from "../../shared/components/UIElements/LoadingSpinnerOverlayRegular";
import {
  AuthContextProps,
  KanbanFilledData,
  SettingsContextProps,
  NotePopCustData,
  CustomerData,
} from "../../shared/data/types";

import "./SaleDash.scss";
import { ChatsOmnichannel } from "../../shared/components/UIElements/ChatsOmnichannel";

interface SaleDashProps {
  type:
    | "SALES"
    | "PROJECT"
    | "INSTALLATION"
    | "AFTER_SALES"
    | "PRE_SALES"
    | "ADMINISTRATIVE";
  active: boolean;
  closeHandler: () => void;
  kanbanData: KanbanFilledData;
  loadedItem: KanbanFilledData["items"][0];
  activeNote?: string;
  auth: AuthContextProps;
  settingsCtx: SettingsContextProps;
  setLoadedData: React.Dispatch<React.SetStateAction<KanbanFilledData>>;
  setScheduleData?: React.Dispatch<React.SetStateAction<NotePopCustData[]>>;
  setActiveItem: React.Dispatch<React.SetStateAction<string>>;
  fetchId: string;
  setUpdateColumnsTotals?: React.Dispatch<React.SetStateAction<string[]>>;
  isManagerView?: boolean;
  isTeamLoaded?: boolean;
}

export const SaleDash = (props: SaleDashProps) => {
  const {
    active,
    closeHandler,
    loadedItem,
    auth,
    setLoadedData,
    setActiveItem,
    activeNote = "",
    fetchId,
    kanbanData,
    settingsCtx,
    type,
    setScheduleData,
    setUpdateColumnsTotals,
    isManagerView = false,
    isTeamLoaded = false,
  } = props;
  const { isLoading, error, sendRequest, clearError } = useHttpClient();
  const [windowWidth, setWindowWidth] = useState<number>(window?.innerWidth);
  const [tab, setTab] = useState<number>(0);
  const [memory, setMemory] = useState<KanbanFilledData["items"][0]>(null);
  const ip = settingsCtx?.individualPermissions?.find((ip) => {
    return ip?.user?.toString() === auth?.userId;
  });
  const isManager =
    (type === "SALES" && ip?.salesManager) ||
    (type === "PROJECT" && ip?.projectManager) ||
    (type === "INSTALLATION" && ip?.installationManager) ||
    (type === "AFTER_SALES" && ip?.afterSalesManager) ||
    (type === "PRE_SALES" && ip?.preSalesManager) ||
    (type === "ADMINISTRATIVE" && ip?.administrativeManager) ||
    false;
  const canAccessPropList =
    settingsCtx.individualPermissions
      ?.filter((ip) => {
        return ip.proposalsList;
      })
      ?.map((ip) => {
        return ip?.user;
      })
      .includes(auth.userId) ||
    auth.type !== "VENDOR" ||
    isManager;

  useEffect(() => {
    if (!!loadedItem) {
      setMemory(loadedItem);
    }
  }, [loadedItem]);

  const resizeHandler = () => {
    setWindowWidth(window?.innerWidth);
  };

  useEffect(() => {
    window.addEventListener("resize", resizeHandler);
    resizeHandler();

    var timer = setTimeout(() => {
      resizeHandler();
    }, 1000);

    return () => {
      window.removeEventListener("resize", resizeHandler);
      clearTimeout(timer);
    };
  }, []);

  const changeTabHandler = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTab(newValue);
  };

  const isSmallWidth = windowWidth < 400;

  return (
    <div className={`sale-dash ${active ? "sale-dash--active" : ""}`}>
      <ModalError error={error} onClear={clearError} />
      <ClickAwayListener
        mouseEvent="onMouseDown"
        onClickAway={() => {
          if (active) {
            closeHandler();
          }
        }}
      >
        <div className="sale-dash__content">
          {isLoading && <LoadingSpinnerOverlayRegular image />}
          <div className="sale-dash__controllers">
            <IconButton
              size="small"
              title="Opções"
              className={`table__item-action`}
              onClick={() => {
                if (active) {
                  closeHandler();
                }
              }}
            >
              <CloseRoundedIcon style={{ fontSize: "22px" }} />
            </IconButton>
          </div>
          <div className="sale-dash__col-left">
            <SaleDashGenData
              auth={auth}
              fetchId={fetchId}
              memory={memory}
              setLoadedData={setLoadedData}
              sendRequest={sendRequest}
              kanbanData={kanbanData}
              setActiveItem={setActiveItem}
              settingsCtx={settingsCtx}
              type={type}
              setUpdateColumnsTotals={setUpdateColumnsTotals}
              isTeamLoaded={isTeamLoaded}
              isManagerView={isManagerView}
            />
            {auth.signupPlan === "ENTERPRISE" && !!memory && (
              <SaleDashChecklist
                auth={auth}
                type={type}
                saleId={memory?._id}
                setLoadedData={setLoadedData}
              />
            )}
          </div>
          <div className="sale-dash__col-right">
            <div className="sale-dash__col-right-content">
              <Tabs
                className="sale-dash__col-right-tabs"
                value={tab}
                onChange={changeTabHandler}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
              >
                <Tab
                  style={{ minWidth: "10px" }}
                  title="Notas"
                  label={windowWidth >= 1200 ? "Notas" : ""}
                  icon={<RateReviewRoundedIcon />}
                />
                <Tab
                  style={{
                    minWidth: "10px",
                    display:
                      !canAccessPropList ||
                      type === "AFTER_SALES" ||
                      type === "PRE_SALES"
                        ? "none"
                        : "inherit",
                  }}
                  icon={
                    <FontAwesomeIcon
                      icon={faFileInvoiceDollar}
                      style={{ fontSize: "20px" }}
                    />
                  }
                  title="Propostas"
                  label={windowWidth >= 1200 ? "Propostas" : ""}
                  disabled={
                    !canAccessPropList ||
                    type === "AFTER_SALES" ||
                    type === "PRE_SALES"
                  }
                />
                <Tab
                  style={{
                    minWidth: "10px",
                    display:
                      auth.signupPlan !== "ENTERPRISE" ||
                      type === "AFTER_SALES" ||
                      type === "PRE_SALES"
                        ? "none"
                        : "inherit",
                  }}
                  icon={
                    <FontAwesomeIcon
                      icon={faFileSignature}
                      style={{ fontSize: "20px" }}
                    />
                  }
                  title="Contratos"
                  label={windowWidth >= 1200 ? "Contratos" : ""}
                  disabled={
                    auth.signupPlan !== "ENTERPRISE" ||
                    type === "AFTER_SALES" ||
                    type === "PRE_SALES"
                  }
                />
                <Tab
                  style={{
                    minWidth: "10px",
                    display:
                      auth.signupPlan !== "ENTERPRISE" ? "none" : "inherit",
                  }}
                  title="Arquivos"
                  label={windowWidth >= 1200 ? "Arquivos" : ""}
                  icon={
                    <FontAwesomeIcon
                      icon={faFolderOpen}
                      style={{ fontSize: "20px" }}
                    />
                  }
                  disabled={auth.signupPlan !== "ENTERPRISE"}
                />
                <Tab
                  style={{
                    minWidth: "10px",
                    display:
                      type === "AFTER_SALES" ||
                      type === "PRE_SALES" ||
                      auth.whitelabel === "SERRANA"
                        ? "none"
                        : "inherit",
                    // display: "none",
                  }}
                  title="Financiamentos"
                  label={
                    windowWidth >= 1520
                      ? "Financiamentos"
                      : windowWidth >= 1200
                      ? "Financ."
                      : ""
                  }
                  icon={
                    <FontAwesomeIcon
                      icon={faMoneyBillWave}
                      style={{ fontSize: "20px" }}
                    />
                  }
                  disabled={type === "AFTER_SALES" || type === "PRE_SALES"}
                />
                <Tab
                  style={{
                    minWidth: "10px",
                    display:
                      type !== "AFTER_SALES" ||
                      !auth?.activeModules?.includes(
                        "Gerador de Propostas de Pós-Venda"
                      )
                        ? "none"
                        : "inherit",
                  }}
                  title="Serviços"
                  label={windowWidth >= 1200 ? "Serviços" : ""}
                  icon={<SyncRoundedIcon />}
                  disabled={
                    type !== "AFTER_SALES" ||
                    !auth?.activeModules?.includes(
                      "Gerador de Propostas de Pós-Venda"
                    )
                  }
                />
                <Tab
                  style={{
                    minWidth: "10px",
                    display:
                      (auth.type === "VENDOR" && !isManager) || isSmallWidth
                        ? "none"
                        : "inherit",
                  }}
                  title="Histórico"
                  label={windowWidth >= 1200 ? "Histórico" : ""}
                  icon={<HistoryRoundedIcon />}
                  disabled={auth.type === "VENDOR" && !isManager}
                />
                {/* <Tab
                  style={{
                    minWidth: "10px",
                    // display: auth.type === "VENDOR" && !isManager ? "none" : "inherit",
                  }}
                  title="Conversas"
                  icon={<WhatsAppIcon />}
                  // disabled={auth.type === "VENDOR" && !isManager}
                /> */}
              </Tabs>
              {tab === 0 && (
                <SaleDashNotes
                  settingsCtx={settingsCtx}
                  auth={auth}
                  type={type}
                  itemId={memory?._id}
                  customerId={memory?.customer?.id}
                  activeNote={activeNote}
                  setScheduleData={setScheduleData}
                />
              )}
              {tab === 1 && canAccessPropList && (
                <SaleDashProposals
                  memory={memory}
                  auth={auth}
                  itemId={memory?._id}
                />
              )}
              {tab === 2 && (
                <SaleDashContracts
                  contracts={memory?.contracts}
                  auth={auth}
                  itemId={memory?._id}
                  item={memory}
                  setKanbanData={setLoadedData}
                />
              )}
              {tab === 3 && <SaleDashFiles cid={memory?.customer?.id} />}
              {tab === 4 && !auth.activeModules?.includes("Marketplace") && (
                <SaleDashFinancings
                  auth={auth}
                  settingsCtx={settingsCtx}
                  customerId={memory?.customer?.id}
                  type={memory?.type}
                  customer={memory?.customer}
                />
              )}
              {tab === 4 && auth.activeModules?.includes("Marketplace") && (
                <SaleDashFinancingsMarketplace
                  auth={auth}
                  settingsCtx={settingsCtx}
                  customerId={memory?.customer?.id}
                  type={memory?.type}
                  customer={memory?.customer}
                />
              )}
              {tab === 5 && (
                <SaleDashServices
                  auth={auth}
                  memory={memory}
                  itemId={memory?._id}
                  setLoadedData={setLoadedData}
                />
              )}
              {tab === 6 && (
                <SaleDashHistory
                  auth={auth}
                  customerId={memory?.customer?.id}
                />
              )}
              {/* {tab === 7 && (
                <ChatsOmnichannel
                  auth={auth}
                  customer={memory.customer as unknown as CustomerData}
                  settingsCtx={settingsCtx}
                />
              )} */}
            </div>
          </div>
        </div>
      </ClickAwayListener>
    </div>
  );
};
